import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { Container, Dialog, DialogContent, Grid, Tooltip } from '@mui/material'
import { DataGridPro, GridColumns } from '@mui/x-data-grid-pro'
import { useEffect, useState } from 'react'
import { UpTransition } from '@components/animations'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import {
  declineEmail,
  getEmails,
  setMssFilters,
} from '@state/reducers/mssReducer'
import { MssProviderEmailDto } from '@/services/dtos'
import MssEmailForm from '../forms/MssEmailForm'
import moment from 'moment'
import AttachmentIcon from '@mui/icons-material/Attachment'
import MssTableToolbar from './Toolbars/MssTableToolbar'
import MssRequestForm from '../forms/MssRequestForm'
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt'
import ConfirmDialog from '../dialogs/ConfirmDialog'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'

export default function MssTable() {
  const [emailOpen, setEmailOpen] = useState<MssProviderEmailDto | null>(null)
  const [processedEmail, setProcessedEmail] =
    useState<MssProviderEmailDto | null>(null)
  const [declinedEmail, setDeclinedEmail] =
    useState<MssProviderEmailDto | null>(null)

  const dispatch = useAppDispatch()
  const { emails, loading, filters } = useAppSelector(({ mss }) => ({
    emails: mss.emails,
    loading: mss.loading,
    filters: mss.filters,
  }))

  useEffect(() => {
    if (filters.doctorId) {
      dispatch(getEmails())
    }
  }, [filters])

  const actionBtnObj = [
    {
      id: 1,
      btnName: 'Ouvrir l\'email',
      icon: <OpenInNewIcon color="primary" />,
      action: (row: MssProviderEmailDto) => setEmailOpen(row),
      actionKey: 'openEmail',
    },
    {
      id: 2,
      btnName: 'Transformer en demande',
      icon: <AssignmentTurnedInIcon color="primary" />,
      action: (row: MssProviderEmailDto) => setProcessedEmail(row),
      actionKey: 'processedEmail',
    },
    {
      id: 3,
      btnName: 'Rejeter',
      icon: <DoNotDisturbAltIcon color="primary" />,
      action: (row: MssProviderEmailDto) => setDeclinedEmail(row),
      actionKey: 'declinedEmail',
    },
  ]

  const columns: GridColumns = [
    {
      field: 'actions',
      headerName: 'Actions',
      width: 130,
      sortable: false,
      renderCell: (params) => {
        const email = params.row
        return (
          <div className="flex space-x-2">
            {actionBtnObj.reduce((acc, el) => {
              acc.push(
                <Tooltip key={email.id + '-' + el.btnName} title={el.btnName}>
                  <div
                    className={'cursor-pointer p-1 hover:bg-gray-200 rounded'}
                    onClick={(e) => {
                      e.stopPropagation()
                      const action = el.action.bind({})
                      action(email)
                    }}
                  >
                    {el.icon}
                  </div>
                </Tooltip>,
              )
              return acc
            }, new Array<React.ReactElement>())}
          </div>
        )
      },
    },
    {
      field: 'messageId',
      headerName: 'Identifiant',
      width: 140,
    },
    {
      field: 'dateTime',
      headerName: 'Date',
      width: 140,
      valueGetter: ({ row }) => moment(row.dateTime).format('DD/MM/YYYY HH:mm'),
    },
    {
      field: 'subject',
      headerName: 'Sujet',
      width: 200,
    },
    {
      field: 'attachments',
      headerName: 'Pièces jointes',
      width: 120,
      renderCell: ({ row }) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {row.attachments.length}{' '}
            <AttachmentIcon style={{ marginLeft: 4 }} />
          </div>
        )
      },
    },
    {
      field: 'addressesFrom',
      headerName: 'Expéditeur',
      width: 150,
      valueGetter: ({ row }) => {
        const fromAddress = (row as MssProviderEmailDto).addresses.find(
          (address) => address.type === 'FROM',
        )
        return fromAddress ? fromAddress.name : ''
      },
    },
  ]

  return (
    <Container maxWidth={false}>
      <Grid container spacing={4} className="h-[calc(100vh-100px)]">
        <Grid item xs={12} className="h-full">
          <div className="h-full w-full bg-white rounded-t-[15px] rounded-b-[25px] overflow-hidden">
            <DataGridPro
              density="standard"
              className="h-full w-full"
              pagination
              paginationMode="server"
              pageSize={20}
              onPageChange={(page) => {
                dispatch(setMssFilters({ page }))
              }}
              components={{
                Toolbar: MssTableToolbar,
              }}
              columns={columns}
              rows={emails.datas}
              rowCount={emails.totalCount}
              disableSelectionOnClick
              disableMultipleSelection
              loading={loading}
              getRowId={(row) => row.messageId}
            />
          </div>
        </Grid>
      </Grid>

      <Dialog
        fullScreen
        open={!!processedEmail}
        onClose={() => setProcessedEmail(null)}
        TransitionComponent={UpTransition}
        slotProps={{ backdrop: {} }}
      >
        <DialogContent>
          {!!processedEmail && (
            <MssRequestForm
              onClose={() => setProcessedEmail(null)}
              email={processedEmail}
              doctorId={filters.doctorId}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!emailOpen}
        onClose={() => setEmailOpen(null)}
        TransitionComponent={UpTransition}
        slotProps={{ backdrop: {} }}
      >
        <DialogContent>
          {!!emailOpen && (
            <MssEmailForm
              onClose={() => setEmailOpen(null)}
              email={emailOpen}
            />
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        title="Rejeter"
        message="Voulez-vous vraiment rejeter cet email ?"
        open={!!declinedEmail}
        onConfirm={() => {
          if (declinedEmail) {
            dispatch(declineEmail(declinedEmail.messageId))
              .unwrap()
              .then(() =>
                dispatch(
                  enqueueSnackbar({
                    message: 'L\'email a été rejeté avec succès',
                    options: { variant: 'success' },
                  }),
                ),
              )
              .finally(() => {
                setDeclinedEmail(null)
                dispatch(getEmails())
              })
          }
        }}
        onCancel={() => {
          setDeclinedEmail(null)
        }}
      />
    </Container>
  )
}
